import { Injectable } from '@angular/core';
import { ACTIVITY_LOG_AREAS } from '../../components/pages/activity-log/areas/activity-log-areas';
import { AppMenuItem } from '../../models/menu.model';
import { AuthenticationService } from '../../services/permission/authentication.service';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private menu: AppMenuItem[] = [];

  constructor(private readonly authService: AuthenticationService) {
    this.buildMenu();
  }

  getMenu(): AppMenuItem[] {
    // Always rebuild the menu to ensure it reflects the current user after login/logout.
    this.buildMenu();
    return this.menu;
  }

  private buildMenu(): void {
    const userPermissions = this.getCombinedPermissions();
    const baseMenu = this.getRawMenu();
    let filteredMenu = this.filterMenuByPermissions(baseMenu, userPermissions);

    this.menu = filteredMenu;
  }

  private getCombinedPermissions(): string[] {
    return this.authService.getUserPermissions();
  }

  private filterMenuByPermissions(menu: AppMenuItem[], permissions: string[]): AppMenuItem[] {
    return (
      menu
        .filter((item) => {
          // First filter children recursively
          if (item.items) {
            item.items = this.filterMenuByPermissions(item.items, permissions);
          }

          // Always keep category headers for now, we'll filter them out later if needed
          if (item.isCategoryHeader) {
            return true;
          }

          // If no permissions required, allow the item
          if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
            return true;
          }

          // Check if user has all required permissions
          return item.requiredPermissions.every((p) => permissions.includes(p));
        })
        // Filter out category headers that have no children after permission filtering
        .filter((item) => !item.isCategoryHeader || (item.items && item.items.length > 0))
        // Filter out empty parent items that might have lost all their children
        .filter((item) => !item.items || item.items.length > 0)
    );
  }

  private getRawMenu(): AppMenuItem[] {
    return [
      {
        label: 'menu.rental.shortThermRental',
        icon: 'pi pi-fw pi-home',
        items: [
          {
            label: 'menu.rental.overView',
            icon: 'pi pi-fw pi-eye',
            routerLink: ['/overview-st'],
            requiredPermissions: ['short-term-overview']
          },
          {
            label: 'menu.rental.bookingOverview',
            icon: 'pi pi-fw pi-book',
            routerLink: ['/booking-overview'],
            requiredPermissions: ['booking']
          },
          {
            label: 'menu.rental.presenceOverview',
            icon: 'pi pi-fw pi-wifi',
            routerLink: ['/presence'],
            requiredPermissions: ['booking']
          },
          {
            label: 'menu.rental.booking',
            icon: 'pi pi-fw pi-calendar-plus',
            routerLink: ['/bookings'],
            requiredPermissions: ['booking']
          },
          {
            label: 'menu.rental.checkins',
            icon: 'pi pi-fw pi-check-circle',
            routerLink: ['/checkins'],
            requiredPermissions: ['checkin']
          },
          {
            label: 'menu.rental.invoicing',
            icon: 'pi pi-fw pi-file-export',
            routerLink: ['/invoices'],
            requiredPermissions: ['invoice']
          }
        ]
      },
      {
        label: 'menu.services.services',
        icon: 'pi pi-fw pi-tags',
        items: [
          {
            label: 'menu.services.parking',
            icon: 'pi pi-fw pi-car',
            routerLink: ['/parking'],
            requiredPermissions: ['parking']
          },
          {
            label: 'menu.services.cleaning',
            icon: 'pi pi-fw pi-sun',
            routerLink: ['/cleaning'],
            requiredPermissions: ['cleaning']
          },
          {
            // One entry for everyone with the view permission; managers land on the
            // editor, staff on their own read/apply-only view (role-based target).
            label: 'menu.services.schedulePlanner',
            icon: 'pi pi-fw pi-calendar-clock',
            routerLink: [this.authService.hasPermission('cleaning-schedule') ? '/schedule-planner' : '/schedule-planner/staff'],
            requiredPermissions: ['cleaning-schedule-view']
          },
          {
            label: 'menu.services.cleaningManagement',
            icon: 'pi pi-fw pi-cog',
            routerLink: ['/cleaning-management'],
            requiredPermissions: ['cleaning-request']
          },
          {
            label: 'menu.services.maintenance',
            icon: 'pi pi-fw pi-wrench',
            routerLink: ['/maintenance'],
            requiredPermissions: ['cleaning']
          },
          {
            label: 'menu.services.arrivalAndDeparture',
            icon: 'pi pi-fw pi-compass',
            routerLink: ['/arrival-departure'],
            requiredPermissions: ['arrival-departures']
          },
          {
            label: 'menu.issues.issues',
            icon: 'pi pi-fw pi-clipboard',
            routerLink: ['/issues'],
            requiredPermissions: ['issue']
          },
          {
            label: 'menu.services.calendar',
            icon: 'pi pi-fw pi-calendar',
            routerLink: ['/calendar'],
            requiredPermissions: ['calendar']
          },
          {
            label: 'menu.services.coupons',
            icon: 'pi pi-fw pi-ticket',
            routerLink: ['/coupons'],
            requiredPermissions: ['coupon']
          },
          // A Stay reggeli-rendelések szerepkör-halmaz alapján látszik, nem jogosultságból,
          // ezért itt dől el a láthatósága — ugyanabból az egy metódusból, mint a route guard.
          ...(this.authService.canAccessStayOrders()
            ? [
                {
                  label: 'menu.services.stayOrders',
                  icon: 'pi pi-fw pi-shopping-bag',
                  routerLink: ['/stay-orders']
                }
              ]
            : [])
        ]
      },
      // Naplózás: területenkénti aktivitás-naplók, CSAK SUPERADMIN-nak. A menüpontok az
      // ACTIVITY_LOG_AREAS-ből jönnek — új terület (host, takarító, karbantartás) = új konfig ott.
      ...(this.authService.isSuperAdmin()
        ? [
            {
              label: 'menu.logging.logging',
              icon: 'pi pi-fw pi-history',
              items: ACTIVITY_LOG_AREAS.map((area) => ({
                label: area.menuLabelKey,
                icon: area.menuIcon,
                routerLink: ['/logging', area.slug]
              }))
            }
          ]
        : []),
      {
        label: 'menu.management.management', // Parent for Management section
        icon: 'pi pi-fw pi-server', // Icon for Management
        items: [
          // {
          //   label: 'menu.management.users',
          //   icon: 'pi pi-fw pi-users', // Existing icon
          //   routerLink: ['/users'],
          //   requiredPermissions: ['user']
          // },
          // {
          //   label: 'menu.management.infra', // Sub-parent for Infrastructure
          //   icon: 'pi pi-fw pi-building', // Icon for Infrastructure section
          //   requiredPermissions: ['master-data'], // General permission for infra section
          //   items: [
          //     {
          //       label: 'menu.management.flatType',
          //       icon: 'pi pi-fw pi-tags',
          //       routerLink: ['/flat-type'],
          //       requiredPermissions: ['master-data']
          //     },
          //     {
          //       label: 'menu.management.cleaningTypes',
          //       icon: 'pi pi-fw pi-filter-fill',
          //       routerLink: ['/cleaning-type'],
          //       requiredPermissions: ['master-data']
          //     },
          //     {
          //       label: 'menu.management.floors',
          //       icon: 'pi pi-fw pi-clone',
          //       routerLink: ['/floor'],
          //       requiredPermissions: ['master-data']
          //     },
          //     {
          //       label: 'menu.management.taxes',
          //       icon: 'pi pi-fw pi-percentage',
          //       routerLink: ['/tax'],
          //       requiredPermissions: ['master-data']
          //     },
          //     {
          //       label: 'menu.management.units',
          //       icon: 'pi pi-fw pi-th-large',
          //       routerLink: ['/unit'],
          //       requiredPermissions: ['master-data']
          //     },
          //     {
          //       label: 'menu.management.meters',
          //       icon: 'pi pi-fw pi-bolt',
          //       routerLink: ['/meters'],
          //       requiredPermissions: ['meter']
          //     }
          //   ]
          // }
        ]
      }
    ];
  }
}
